import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Filesystem } from '@capacitor/filesystem';
import imageCompression from 'browser-image-compression';

/**
 * Check if app is running inside Capacitor native container (Android / iOS)
 */
export const isNativePlatform = () => {
  return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
};

/**
 * Compress Image (Resizes & Reduces file size while maintaining high quality)
 * Works for both Web File objects and Capacitor Blobs.
 */
export const compressImageFile = async (file, options = {}) => {
  if (!file) return file;
  
  // Default compression options
  const defaultOptions = {
    maxSizeMB: 1.5,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
    fileType: 'image/jpeg',
    initialQuality: 0.85,
    ...options
  };

  try {
    const compressedBlob = await imageCompression(file, defaultOptions);
    const compressedFile = new File([compressedBlob], file.name || `photo_${Date.now()}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now()
    });
    console.log(`📸 Image compressed: ${(file.size / 1024 / 1024).toFixed(2)}MB -> ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
    return compressedFile;
  } catch (error) {
    console.warn('Image compression fallback to original:', error.message);
    return file;
  }
};

/**
 * Convert Capacitor Camera Photo Result to standard JS File object
 */
const convertPhotoToFile = async (photo) => {
  let blob;
  if (photo.webPath) {
    const response = await fetch(photo.webPath);
    blob = await response.blob();
  } else if (photo.base64String) {
    const byteCharacters = atob(photo.base64String);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    blob = new Blob([byteArray], { type: `image/${photo.format || 'jpeg'}` });
  } else if (photo.path) {
    const fileData = await Filesystem.readFile({ path: photo.path });
    const byteCharacters = atob(fileData.data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    blob = new Blob([byteArray], { type: `image/${photo.format || 'jpeg'}` });
  }

  const fileName = `photo_${Date.now()}.${photo.format || 'jpg'}`;
  const rawFile = new File([blob], fileName, { type: `image/${photo.format || 'jpeg'}` });
  
  // Auto-compress photo before return
  return await compressImageFile(rawFile);
};

/**
 * Capture Photo using Native Camera or Web Fallback
 */
export const capturePhotoWithCamera = async () => {
  if (isNativePlatform()) {
    try {
      const photo = await Camera.getPhoto({
        resultType: CameraResultType.Uri,
        source: CameraSource.Camera,
        quality: 90,
        allowEditing: true
      });
      return await convertPhotoToFile(photo);
    } catch (err) {
      if (err.message !== 'User cancelled photos app') {
        console.error('Camera capture error:', err);
      }
      return null;
    }
  }

  // Web input fallback
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        const compressed = await compressImageFile(file);
        resolve(compressed);
      } else {
        resolve(null);
      }
    };
    input.click();
  });
};

/**
 * Select Photo from Native Gallery or Web Fallback
 */
export const selectPhotoFromGallery = async () => {
  if (isNativePlatform()) {
    try {
      const photo = await Camera.getPhoto({
        resultType: CameraResultType.Uri,
        source: CameraSource.Photos,
        quality: 90,
        allowEditing: true
      });
      return await convertPhotoToFile(photo);
    } catch (err) {
      if (err.message !== 'User cancelled photos app') {
        console.error('Gallery pick error:', err);
      }
      return null;
    }
  }

  // Web input fallback
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        const compressed = await compressImageFile(file);
        resolve(compressed);
      } else {
        resolve(null);
      }
    };
    input.click();
  });
};

/**
 * Capture or Pick Video
 */
export const selectOrRecordVideo = async (sourceType = 'gallery') => {
  if (isNativePlatform() && Camera.pickVideos) {
    try {
      const videos = await Camera.pickVideos({
        limit: 1
      });
      if (videos?.photos?.[0]) {
        const video = videos.photos[0];
        const response = await fetch(video.webPath);
        const blob = await response.blob();
        return new File([blob], `video_${Date.now()}.mp4`, { type: 'video/mp4' });
      }
    } catch (err) {
      console.warn('Native video picker fallback to web input:', err.message);
    }
  }

  // Web file input fallback
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/mp4,video/quicktime,video/webm';
    if (sourceType === 'camera') {
      input.capture = 'environment';
    }
    input.onchange = (e) => {
      const file = e.target.files[0];
      resolve(file || null);
    };
    input.click();
  });
};
